import './styles.css';
import { CSP_TESTSEITE } from '../shared/csp.js';
import { modus, serverErreichbar, setzeModus } from './api.js';
import { anmeldefeld } from './ansichten/anmeldefeld.js';
import { auswertung } from './ansichten/auswertung.js';
import { bildschirm } from './ansichten/bildschirm.js';
import { einrichtung } from './ansichten/einrichtung.js';
import { fotos } from './ansichten/fotos.js';
import { fangeInstallAufforderung, installation, pruefeInstallation } from './ansichten/installation.js';
import { kamera } from './ansichten/kamera.js';
import { passkey } from './ansichten/passkey.js';
import { pc } from './ansichten/pc.js';
import { pruefePushAbo, push } from './ansichten/push.js';
import { druck, qr, qrZiel } from './ansichten/qr.js';
import { cspTestOhneSeite, sicherheit } from './ansichten/sicherheit.js';
import { pruefeDauerhaftenSpeicherStill, speicher } from './ansichten/speicher.js';
import { start } from './ansichten/start.js';
import { tastatur } from './ansichten/tastatur.js';
import { zurueck } from './ansichten/zurueck.js';
import { h } from './dom.js';
import { ereignisse, ladeErgebnisse, melde, sendeAusgang, wartendeErgebnisse } from './ergebnisse.js';
import { arbeite, beimStart } from './foto-warteschlange.js';
import { erkenneModus, registriereStart, startInfo, type StartInfo } from './geraet.js';
import { pruefeTestbestand } from './offline-daten.js';
import { navigiere, route, setzeWache, starteRouter, type Ansicht } from './router.js';
import { holeStand, istAngemeldet, meldePlattform, sicherstelleKontext } from './sitzung.js';
import { db, schreibe } from './speicher.js';

const ANSICHTEN: Record<string, [string, Ansicht]> = {
  installation: ['Installation', installation],
  passkey: ['Passkey', passkey],
  anmeldefeld: ['Passkey im Anmeldefeld', anmeldefeld],
  kamera: ['Kamera und Taschenlampe', kamera],
  qr: ['QR-Aufkleber', qr],
  bildschirm: ['Bildschirm bleibt an', bildschirm],
  zurueck: ['Zurück-Geste', zurueck],
  tastatur: ['Tastatur', tastatur],
  push: ['Push', push],
  speicher: ['Speicher und Offline', speicher],
  fotos: ['Foto-Warteschlange', fotos],
  sicherheit: ['CSP und Trusted Types', sicherheit],
  pc: ['Büro-PC', pc],
};

function registriereRouten(): void {
  route('/', 'Übersicht', start);
  route('/einrichtung', 'Gerät einrichten', einrichtung);
  route('/auswertung', 'Auswertung', auswertung);
  route('/druck', 'QR-Aufkleber', druck);
  route('/q/:kuerzel/:schluessel', 'QR-Aufkleber geöffnet', qrZiel);
  // Die echte Testseite liefert der Server als eigenes Dokument; hierher kommt nur, wer sie nicht geladen hat.
  route(CSP_TESTSEITE, 'CSP und Trusted Types', cspTestOhneSeite);
  route('/pruefung/:ansicht', 'Prüfung', async (ziel, params, suche) => {
    const eintrag = ANSICHTEN[params.ansicht ?? ''];
    if (!eintrag) {
      ziel.append(h('h1', {}, 'Prüfung nicht gefunden'), h('a', { href: '/' }, 'Zur Übersicht'));
      return;
    }
    document.title = `${eintrag[0]} – TFM Gerätetest`;
    return eintrag[1](ziel, params, suche);
  });
  // Ohne Anmeldung nur Einrichtung, die Ergebnisseite nach dem QR-Scan sowie
  // Auswertung und Druckseite (dort genügt der Zugangscode, siehe ansichten/zugang.ts).
  setzeWache(async (pfad) => {
    const ohneSchraegstrich = pfad.length > 1 ? pfad.replace(/\/+$/, '') : pfad;
    if (['/einrichtung', '/auswertung', '/druck'].includes(ohneSchraegstrich) || pfad.startsWith('/q/')) return null;
    return (await istAngemeldet()) ? null : `/einrichtung?weiter=${encodeURIComponent(pfad + location.search)}`;
  });
}

function registriereServiceWorker(): void {
  if (!('serviceWorker' in navigator)) return;
  navigator.serviceWorker.register('/sw.js', { scope: '/' }).catch((fehler: unknown) => console.warn('Service Worker nicht registriert', fehler));
  // Tippen auf eine Push-Nachricht bei offener App: Der Service Worker schickt die Zielseite.
  navigator.serviceWorker.addEventListener('message', (e: MessageEvent<{ art?: string; url?: string }>) => {
    if (e.data.art === 'navigiere' && typeof e.data.url === 'string' && e.data.url.startsWith('/')) navigiere(e.data.url);
  });
}

async function zeichneKopfStatus(element: HTMLElement): Promise<void> {
  const wartend = await wartendeErgebnisse();
  const online = navigator.onLine;
  element.textContent = !online ? 'Offline' : wartend > 0 ? `${wartend} wartend` : modus() === 'app' ? 'App' : 'Browser';
  element.className = `kopf-status${!online || wartend > 0 ? ' kopf-status-warn' : ''}`;
}

async function imHintergrund(s: StartInfo): Promise<void> {
  const online = await serverErreichbar();
  const angemeldet = await istAngemeldet();
  if (!online && angemeldet) {
    await melde('offline-start', 'ok', `Ohne Verbindung zum Server gestartet (${modus() === 'app' ? 'installierte App' : 'Browser'}).`, {
      modus: modus(),
      startNr: s.startNr,
      neuGestartet: s.frisch,
    });
  }
  if (angemeldet) {
    await pruefeInstallation();
    if (online) {
      await meldePlattform().catch(() => {});
      await holeStand().catch(() => {});
      await pruefePushAbo().catch(() => {});
    }
    await pruefeDauerhaftenSpeicherStill().catch(() => {});
    await pruefeTestbestand(s).catch((fehler: unknown) => console.warn('Testbestand nicht geprüft', fehler));
    await beimStart();
  }
  await sendeAusgang();
}

function beobachteSichtbarkeit(): void {
  let verstecktSeit: number | undefined;
  document.addEventListener('visibilitychange', () => {
    const jetzt = Date.now();
    void schreibe('zuletztAktiv', new Date(jetzt).toISOString());
    if (document.visibilityState === 'hidden') {
      verstecktSeit = jetzt;
      return;
    }
    // Lag die App lange im Hintergrund, ohne beendet zu werden, auch dann die Offline-Daten prüfen.
    if (verstecktSeit !== undefined && jetzt - verstecktSeit > 60 * 60 * 1000) {
      const zuletztAktivVorher = new Date(verstecktSeit).toISOString();
      void istAngemeldet().then((angemeldet) => {
        // Ohne eingerichtetes Gerät gibt es weder Stand noch Testbestand.
        if (!angemeldet) return;
        void pruefeTestbestand({ ...startInfo(), zuletztAktivVorher });
        void holeStand().catch(() => {});
      });
    }
    verstecktSeit = undefined;
    void sendeAusgang();
    void arbeite();
  });
  window.addEventListener('online', () => {
    void sendeAusgang();
    void arbeite();
  });
}

async function starte(): Promise<void> {
  const inhalt = document.getElementById('inhalt');
  const kopfStatus = document.getElementById('kopf-status');
  if (!inhalt || !kopfStatus) return;
  fangeInstallAufforderung();
  setzeModus(erkenneModus());
  try {
    await db.open();
  } catch (fehler) {
    inhalt.replaceChildren(
      h('h1', {}, 'Speicher nicht verfügbar'),
      h('p', {}, `Die App kann ihren lokalen Speicher nicht öffnen (${(fehler as Error).name}). Privates Surfen ausschalten oder Website-Daten erlauben.`),
    );
    return;
  }
  await ladeErgebnisse();
  const s = await registriereStart();
  registriereServiceWorker();
  if (await istAngemeldet().then((ja) => !ja)) await sicherstelleKontext().catch(() => {});
  registriereRouten();
  starteRouter(inhalt);
  beobachteSichtbarkeit();

  const kopfAktualisieren = () => void zeichneKopfStatus(kopfStatus);
  ereignisse.addEventListener('geaendert', kopfAktualisieren);
  window.addEventListener('online', kopfAktualisieren);
  window.addEventListener('offline', kopfAktualisieren);
  kopfAktualisieren();
  await imHintergrund(s);
}

void starte();
